import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  HelpCircle, ChevronDown, ChevronUp, Search, 
  MessageSquare, Users, Globe, CheckCircle, ArrowRight
} from 'lucide-react';
import Layout from '../../components/layout/Layout';
import PageWrapper, { 
  HeroSection, 
  Section, 
  Badge, 
  Heading, 
  Card, 
  IconWrapper, 
  CTASection 
} from '../../components/layout/PageWrapper';

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const categories = [
    { id: 'general', name: 'General', icon: HelpCircle, gradient: "from-blue-600 to-blue-700" },
    { id: 'technical', name: 'Technical', icon: MessageSquare, gradient: "from-gray-600 to-gray-700" },
    { id: 'pricing', name: 'Pricing', icon: Users, gradient: "from-blue-500 to-blue-600" },
    { id: 'support', name: 'Support', icon: Globe, gradient: "from-gray-500 to-gray-600" }
  ];

  const faqs = {
    general: [
      {
        id: 'gen-1',
        question: 'What is AxonMetric Renewables?',
        answer: 'AxonMetric Renewables is a leading provider of advanced analytics and AI-driven insights for renewable energy assets. We help operators maximize efficiency and reliability of wind, solar, and battery storage systems through comprehensive monitoring and optimization solutions.'
      },
      {
        id: 'gen-2',
        question: 'What types of renewable energy assets do you support?',
        answer: 'We support all major types of renewable energy assets including solar photovoltaic systems, wind farms, battery storage facilities, and hybrid energy systems. Our platform is designed to handle both utility-scale and commercial installations.'
      },
      {
        id: 'gen-3',
        question: 'How long has AxonMetric been in business?',
        answer: 'AxonMetric has been serving the renewable energy industry since 2017. We have successfully deployed our solutions across 13 sites with a combined capacity of over 3.0 GW of renewable energy assets.'
      },
      {
        id: 'gen-4',
        question: 'What makes AxonMetric different from other monitoring solutions?',
        answer: 'Our platform combines advanced AI-driven analytics with real-time monitoring capabilities. We provide predictive maintenance, performance optimization, and automated workflows that help operators identify and resolve issues before they impact production.'
      }
    ],
    technical: [
      {
        id: 'tech-1',
        question: 'What data sources does your platform integrate with?',
        answer: 'Our platform integrates with SCADA systems, weather stations, inverters, sensors, and other IoT devices commonly used in renewable energy installations. We support industry-standard protocols and can work with existing infrastructure.'
      },
      {
        id: 'tech-2',
        question: 'How secure is the data on your platform?',
        answer: 'We implement enterprise-grade security measures including end-to-end encryption, secure data transmission, and compliance with industry standards. All data is stored in secure, redundant cloud infrastructure with regular backups.'
      },
      {
        id: 'tech-3',
        question: 'What kind of analytics and reporting do you provide?',
        answer: 'We provide comprehensive analytics including performance metrics, efficiency analysis, predictive maintenance alerts, and customizable reports. Our AI models can identify patterns and predict potential issues before they occur.'
      },
      {
        id: 'tech-4',
        question: 'Can your platform handle multiple sites?',
        answer: 'Yes, our platform is designed for portfolio management and can handle multiple sites from a single dashboard. You can monitor and manage all your renewable energy assets from one centralized interface.'
      }
    ],
    pricing: [
      {
        id: 'price-1',
        question: 'What is your pricing model?',
        answer: 'Our pricing is based on the capacity and complexity of your renewable energy assets. We offer flexible subscription plans that scale with your portfolio size. Contact our sales team for a customized quote based on your specific needs.'
      },
      {
        id: 'price-2',
        question: 'Do you offer a free trial?',
        answer: 'Yes, we offer a 30-day free trial of our platform so you can experience the full capabilities before making a commitment. Our team will work with you to set up a demo with your actual data.'
      },
      {
        id: 'price-3',
        question: 'Are there any hidden costs?',
        answer: 'No hidden costs. Our pricing is transparent and includes all core features, support, and updates. Additional costs may apply only for custom integrations or specialized consulting services.'
      },
      {
        id: 'price-4',
        question: 'Do you offer volume discounts?',
        answer: 'Yes, we offer volume discounts for operators with multiple sites or large portfolios. The more assets you manage with our platform, the better the pricing becomes.'
      }
    ],
    support: [
      {
        id: 'sup-1',
        question: 'What kind of support do you provide?',
        answer: 'We provide 24/7 technical support through multiple channels including phone, email, and live chat. Our support team includes renewable energy experts who understand your specific challenges and requirements.'
      },
      {
        id: 'sup-2',
        question: 'How quickly do you respond to support requests?',
        answer: 'We typically respond to critical issues within 2 hours and non-critical issues within 24 hours. Our support team is available around the clock to ensure your operations continue smoothly.'
      },
      {
        id: 'sup-3',
        question: 'Do you provide training for your platform?',
        answer: 'Yes, we provide comprehensive training for your team including initial setup, ongoing education, and best practices. We also offer customized training programs tailored to your specific workflows.'
      },
      {
        id: 'sup-4',
        question: 'Can you help with implementation and integration?',
        answer: 'Absolutely. Our team provides full implementation support including data integration, system configuration, and user training. We work closely with your team to ensure a smooth transition.'
      }
    ]
  };

  const filteredFAQs = faqs[activeCategory].filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout header={6} footer={6}>
      <PageWrapper>
        {/* Hero Section */}
        <HeroSection>
          <div className="text-center max-w-4xl mx-auto">
            <Badge>FAQ</Badge>
            
            <Heading level={1} variant="gradient" className="mb-8">
              Frequently Asked
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                Questions
              </span>
            </Heading>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-600 leading-relaxed mb-12 max-w-3xl mx-auto"
            >
              Find answers to common questions about our renewable energy analytics platform, 
              services, and how we can help optimize your energy assets.
            </motion.p>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="max-w-2xl mx-auto"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 text-lg bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                />
              </div>
            </motion.div>
          </div>
        </HeroSection>

        {/* FAQ Categories */}
        <Section padding="py-12" className="border-b border-gray-100">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <category.icon size={20} />
                <span>{category.name}</span>
              </motion.button>
            ))}
          </div>
        </Section>

        {/* FAQ Content */}
        <Section backgroundType="gradient">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {filteredFAQs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  <button
                    onClick={() => toggleItem(faq.id)}
                    className="w-full p-8 text-left flex items-center justify-between hover:bg-white/90 transition-all duration-300"
                  >
                    <h3 className="text-xl font-bold text-gray-800 pr-4">
                      {faq.question}
                    </h3>
                    <div className="flex-shrink-0">
                      {openItems[faq.id] ? (
                        <ChevronUp className="w-6 h-6 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  {openItems[faq.id] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-8 pb-8"
                    >
                      <div className="border-t border-gray-100 pt-6">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>

            {filteredFAQs.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="text-gray-500 text-lg mb-4">No questions found matching your search.</div>
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-teal-600 hover:text-teal-700 font-medium"
                >
                  Clear search
                </button>
              </motion.div>
            )}
          </div>
        </Section>

        {/* Still Have Questions Section */}
        <CTASection
          title="Still Have Questions?"
          description="Can't find what you're looking for? Our support team is here to help you with any questions or concerns."
          primaryButton={
            <a
              href="/contact"
              className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-bold py-4 px-8 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 text-lg"
            >
              <span>Contact Support</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          }
          secondaryButton={
            <a
              href="/contact"
              className="group inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm border border-gray-200 text-gray-700 font-bold py-4 px-8 rounded-2xl hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 text-lg"
            >
              <span>Schedule a Call</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          }
        />

        {/* Quick Links Section */}
        <Section backgroundType="gradient">
          <div className="text-center mb-16">
            <Badge variant="purple">Quick Links</Badge>
            <Heading level={2} variant="gradient">
              Helpful Resources
            </Heading>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Explore our documentation, guides, and resources to get the most out of our platform
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Documentation",
                description: "Comprehensive guides and API documentation",
                icon: CheckCircle,
                gradient: "from-blue-600 to-blue-700"
              },
              {
                title: "Video Tutorials",
                description: "Step-by-step video guides and tutorials",
                icon: MessageSquare,
                gradient: "from-gray-600 to-gray-700"
              },
              {
                title: "Community Forum",
                description: "Connect with other users and share experiences",
                icon: Users,
                gradient: "from-blue-500 to-blue-600"
              }
            ].map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group relative"
              >
                <Card className="text-center p-8">
                  {/* Icon */}
                  <IconWrapper 
                    icon={resource.icon} 
                    size={32} 
                    gradient={resource.gradient}
                    className="mb-6"
                  />
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-gray-900 transition-colors duration-300">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {resource.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </Section>
      </PageWrapper>
    </Layout>
  );
};

export default FAQPage;
